import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { Commands, Results } from '@local/client-contracts';
import { PopupRef, UiIconModel } from '@local/ui-infra';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ContextMenuComponent, ContextMenuData } from '@shared/components';
import { DragData, DragMetadata } from '@shared/directives/drag-and-drop.directive';
import { KeyboardService } from '@shared/services/keyboard.service';
import { isRightClick } from '@shared/utils';
import { getIconModel } from '@shared/utils/set-icon.util';
import { DropdownComponent } from 'src/app/bar/components';
import { ResultAttachmentService } from '../../services/result-attachment.service';
import { KeyName, isEnterKey, splitFileName } from '@local/ts-infra';

@UntilDestroy()
@Component({
  selector: 'result-attachment',
  templateUrl: './result-attachment.component.html',
  styleUrls: ['./result-attachment.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ResultAttachmentComponent implements OnInit, OnDestroy {
  private readonly DEFAULT_FILE_ICON = '/assets/file-icons/general.svg';
  private _model: Results.Attachment;
  private _moreAttachments: Results.Attachment[] = [];
  private keyHandlerId: string;
  isIconEmoji: boolean;
  showCopied: boolean;
  splittedText = '';
  iconModel: UiIconModel;
  backgroundColor = '';
  contextMenuOpened = false;
  _smallWidth = false;
  dragData: DragData = { metadata: undefined, model: undefined };
  activeContextMenu: PopupRef<ContextMenuComponent, ContextMenuData>;

  @Input() resourceId: string;
  @Input() hideIcon: boolean;
  @Input() defaultBackgroundColor: string;
  @Input() openContextMenuOnEnterKey = false;
  @Input() set dragMetadata(metadata: DragMetadata) {
    this.dragData.metadata = metadata;
  }
  @Input() set smallWidth(value: boolean) {
    this._smallWidth = value;
    this.initAttachment();
  }
  @Input() previewAttachment = false;
  @Input() dragAndDrop = true;
  @Input() downloadEnabled = true;
  @Input() set model(value: Results.Attachment) {
    if (!value) return;
    this.initModel(value);
  }
  @Input() set moreAttachments(value: Results.Attachment[]) {
    if (!value.length) return;
    this._moreAttachments = value;
    const text = `+${value.length}`;
    this.initModel({ text, tooltip: text, icon: { lightUrl: this.DEFAULT_FILE_ICON } });
  }

  @Output() invoke = new EventEmitter<Commands.Command>();

  @ViewChild('attachment') attachment: ElementRef;
  @ViewChild('dropdown') dropdown: DropdownComponent;
  @ViewChild('wrapperContainer') wrapperContainer: ElementRef;

  get model(): Results.Attachment {
    return this._model;
  }

  get moreAttachments(): Results.Attachment[] {
    return this._moreAttachments;
  }

  get smallWidth(): boolean {
    return this._smallWidth;
  }

  constructor(
    protected cdr: ChangeDetectorRef,
    protected keyboardService: KeyboardService,
    private resultAttachmentService: ResultAttachmentService
  ) {}

  ngOnInit(): void {
    this.keyHandlerId = this.keyboardService.registerKeyHandler((keys, event) => {
      this.handleKeys(keys, event);
    }, 6);
  }

  ngOnDestroy(): void {
    if (this.keyHandlerId) {
      this.keyboardService.unregisterKeyHandler(this.keyHandlerId);
      this.keyHandlerId = null;
    }
  }

  @HostBinding('class.clickable')
  get clickable(): boolean {
    return (
      (this.dragData?.model && (this.dragData.model.onClick != null || this.dragData.model.onDrag != null)) ||
      this.moreAttachments?.length > 0
    );
  }

  @HostListener('auxclick', ['$event'])
  async onAuxClick(event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    if (isRightClick(event)) {
      if (this.moreAttachments?.length > 0) {
        this.openPopupAttachments();
        return;
      }
      await this.openContextMenu(event);
    }
  }
  @HostListener('click', ['$event'])
  async onClick(event: MouseEvent): Promise<void> {
    event.stopPropagation();
    if (this.moreAttachments?.length > 0) {
      this.openPopupAttachments();
      return;
    }
    const command: Commands.Command = await this.resultAttachmentService.download(this.dragData);
    if (command) {
      this.invoke.emit(command);
    }
  }

  @HostListener('contextmenu', ['$event'])
  async onRightClick(event: MouseEvent): Promise<void> {
    event.stopImmediatePropagation();
    event.preventDefault();
    if (this.moreAttachments?.length > 0) {
      this.openPopupAttachments();
      return;
    }
    await this.openContextMenu(event);
  }

  private initModel(value: Results.Attachment) {
    this.dragData.model = value;
    this._model = value;
    if (value.extension) {
      this._model.icon = this.resultAttachmentService.getAttachmentIcon(value.extension);
    }
    this.iconModel = getIconModel(this.model?.icon);
    this.initAttachment();
  }

  private initAttachment() {
    let first = 12;
    let last = 5;
    if (this.smallWidth) {
      first = last = 3;
      if (this.previewAttachment) {
        first = 6;
      }
    }
    this.splittedText = splitFileName(this.model?.text, this.model?.extension, first, last);
    this.cdr.markForCheck();
  }

  private async openContextMenu(event: { x: number; y: number }) {
    this.activeContextMenu = await this.resultAttachmentService.openContextMenu(event, this.dragData, this.previewAttachment);
    this.contextMenuOpened = true;
    this.cdr.markForCheck();
    this.subscribeToContextMenuClosed();
  }

  private openPopupAttachments() {
    this.resultAttachmentService.openPopupAttachments(this.moreAttachments, this.dragData.metadata, this.downloadEnabled, this.attachment);
  }

  private subscribeToContextMenuClosed() {
    this.activeContextMenu?.destroy$.pipe(untilDestroyed(this)).subscribe(() => {
      this.contextMenuOpened = false;
      this.cdr.markForCheck();
    });
  }

  private handleKeys(keys: Array<KeyName>, event): Promise<void> {
    if (this.openContextMenuOnEnterKey) {
      if (isEnterKey(keys[0])) {
        this.openContextMenu(this.attachment.nativeElement.getBoundingClientRect());
        event.stopPropagation();
        this.cdr.markForCheck();
        return;
      }
    }
  }
}
